// ILIK Supabase Integration
// Uses Supabase Auth user ID (auth.uid()) for all user_cycles operations.
//
// REQUIRED RLS POLICIES on the user_cycles table (add in Supabase Dashboard):
//   Table: user_cycles
//   1. INSERT policy  — name: "Users can insert own row"
//      USING expression: (auth.uid() = user_id)
//   2. SELECT policy  — name: "Users can read own row"
//      USING expression: (auth.uid() = user_id)
//   3. UPDATE policy  — name: "Users can update own row"
//      USING expression: (auth.uid() = user_id)
//   How to add:
//     Supabase Dashboard → Table Editor → user_cycles → RLS → New Policy → Custom → paste expression above

import { createClient } from '@supabase/supabase-js'

let supabase = null
let isSupabaseConnected = false

try {
  const supabaseUrl =
    process.env.REACT_APP_SUPABASE_URL ||
    process.env.EXPO_PUBLIC_SUPABASE_URL

  const supabaseAnonKey =
    process.env.REACT_APP_SUPABASE_ANON_KEY ||
    process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY

  if (supabaseUrl && supabaseAnonKey) {
    supabase = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: false,
      },
    })
    isSupabaseConnected = true
    console.log('✅ Supabase client ready')
  }
} catch (error) {
  console.error('❌ Supabase init error:', error.message)
}

// Get the authenticated user's ID from Supabase Auth
const getAuthUserId = async () => {
  if (!supabase) return null
  try {
    const { data: { user } } = await supabase.auth.getUser()
    return user?.id || null
  } catch {
    return null
  }
}

// Save / upsert cycle data for the authenticated user
export const saveUserCycleToSupabase = async (email, lastPeriodStartDate, cycleLength, periodLength) => {
  // Always persist to localStorage as offline fallback
  try {
    localStorage.setItem('user_data', JSON.stringify({
      email,
      last_period_start_date: lastPeriodStartDate,
      cycle_length: cycleLength,
      period_length: periodLength,
    }))
  } catch (err) {
    console.error('localStorage write error:', err)
  }

  if (!supabase || !isSupabaseConnected) return null

  const userId = await getAuthUserId()
  if (!userId) {
    console.warn('⚠️ saveUserCycleToSupabase: no authenticated user — data saved to localStorage only')
    return null
  }

  try {
    // Check if a row already exists for this user
    const { data: existing, error: selectError } = await supabase
      .from('user_cycles')
      .select('id')
      .eq('user_id', userId)
      .maybeSingle()

    if (selectError) {
      console.error('SELECT error:', selectError.code, selectError.message)
      return null
    }

    if (existing) {
      // UPDATE
      const { error } = await supabase
        .from('user_cycles')
        .update({
          last_period_start_date: lastPeriodStartDate,
          cycle_length: cycleLength,
          period_length: periodLength,
        })
        .eq('user_id', userId)

      if (error) {
        console.error('UPDATE error:', error.code, error.message)
        return null
      }
      console.log('✅ user_cycles UPDATED for', userId)
    } else {
      // INSERT
      const { error } = await supabase
        .from('user_cycles')
        .insert({
          user_id: userId,
          Email: email,
          last_period_start_date: lastPeriodStartDate,
          cycle_length: cycleLength,
          period_length: periodLength,
        })

      if (error) {
        console.error('INSERT error:', error.code, error.message)
        if (error.code === '42501') {
          console.warn(
            '⚠️ RLS is blocking the insert. Add these policies in Supabase Dashboard → user_cycles → RLS:\n' +
            '  INSERT: auth.uid() = user_id\n' +
            '  SELECT: auth.uid() = user_id\n' +
            '  UPDATE: auth.uid() = user_id'
          )
        }
        return null
      }
      console.log('✅ user_cycles INSERTED for', userId)
    }

    return true
  } catch (err) {
    console.error('saveUserCycleToSupabase error:', err.message)
    return null
  }
}

// Load cycle data for the authenticated user from Supabase
// Returns null if not found; caller falls back to backend/localStorage
export const loadUserCycleFromSupabase = async () => {
  if (!supabase || !isSupabaseConnected) {
    // Fall back to localStorage only
    return parseLocalStorage()
  }

  const userId = await getAuthUserId()
  if (!userId) {
    return parseLocalStorage()
  }

  try {
    const { data, error } = await supabase
      .from('user_cycles')
      .select('last_period_start_date, cycle_length, period_length, Email')
      .eq('user_id', userId)
      .maybeSingle()

    if (error) {
      console.error('loadUserCycleFromSupabase SELECT error:', error.code, error.message)
      return parseLocalStorage()
    }

    if (data) {
      console.log('✅ Loaded user_cycles from Supabase for', userId)
      // Sync to localStorage so offline access works
      try {
        localStorage.setItem('user_data', JSON.stringify({
          email: data.Email,
          last_period_start_date: data.last_period_start_date,
          cycle_length: data.cycle_length,
          period_length: data.period_length,
        }))
      } catch {}
      return {
        email: data.Email,
        lastPeriodStartDate: data.last_period_start_date,
        cycleLength: data.cycle_length,
        periodLength: data.period_length,
      }
    }
  } catch (err) {
    console.error('loadUserCycleFromSupabase error:', err.message)
  }

  return parseLocalStorage()
}

// Update cycle data for the authenticated user (called when settings change)
export const updateUserCycleInSupabase = async (lastPeriodStartDate, cycleLength, periodLength) => {
  // Update localStorage
  try {
    const existing = JSON.parse(localStorage.getItem('user_data') || '{}')
    localStorage.setItem('user_data', JSON.stringify({
      ...existing,
      last_period_start_date: lastPeriodStartDate,
      cycle_length: cycleLength,
      period_length: periodLength,
    }))
  } catch {}

  if (!supabase || !isSupabaseConnected) return false

  const userId = await getAuthUserId()
  if (!userId) return false

  try {
    const { error } = await supabase
      .from('user_cycles')
      .update({
        last_period_start_date: lastPeriodStartDate,
        cycle_length: cycleLength,
        period_length: periodLength,
      })
      .eq('user_id', userId)

    if (error) {
      console.error('updateUserCycleInSupabase error:', error.message)
      return false
    }
    console.log('✅ user_cycles updated for', userId)
    return true
  } catch (err) {
    console.error('updateUserCycleInSupabase error:', err.message)
    return false
  }
}

// Parse localStorage fallback
const parseLocalStorage = () => {
  try {
    const raw = localStorage.getItem('user_data')
    if (!raw) return null
    const d = JSON.parse(raw)
    return {
      email: d.email,
      lastPeriodStartDate: d.last_period_start_date,
      cycleLength: d.cycle_length,
      periodLength: d.period_length,
    }
  } catch {
    return null
  }
}

export { supabase, isSupabaseConnected }

// Update is_premium flag for the authenticated user
export const updateIsPremium = async (premium = true) => {
  if (!supabase) return false
  const userId = await getAuthUserId()
  if (!userId) return false
  try {
    const { error } = await supabase
      .from('user_cycles')
      .update({ is_premium: premium })
      .eq('user_id', userId)
    if (error) {
      console.error('updateIsPremium error:', error.message)
      return false
    }
    console.log(`✅ is_premium set to ${premium} for`, userId)
    return true
  } catch (err) {
    console.error('updateIsPremium error:', err.message)
    return false
  }
}

// ── Partner Links ────────────────────────────────────────────────
// Required Supabase table (run in SQL editor):
//   CREATE TABLE partner_links (
//     id uuid DEFAULT gen_random_uuid() PRIMARY KEY,
//     token text UNIQUE NOT NULL,
//     user_id uuid NOT NULL REFERENCES auth.users(id),
//     created_at timestamptz DEFAULT now()
//   );
// RLS policies needed:
//   INSERT: auth.uid() = user_id
//   SELECT: auth.uid() = user_id

export const getPartnerCycleData = async (token) => {
  console.log('🔍 getPartnerCycleData: looking up token:', token)
  if (!supabase) {
    console.warn('⚠️ getPartnerCycleData: Supabase client not initialised')
    return null
  }
  try {
    const { data, error } = await supabase.rpc('get_partner_cycle_data', { p_token: token })
    if (error) {
      console.error('❌ getPartnerCycleData RPC error:', error.code, error.message)
      return null
    }
    console.log('📦 getPartnerCycleData result:', data)
    return data || null
  } catch (err) {
    console.error('❌ getPartnerCycleData exception:', err.message)
    return null
  }
}

export const checkIsPremium = async () => {
  if (!supabase) return false
  const userId = await getAuthUserId()
  if (!userId) return false
  try {
    const { data, error } = await supabase
      .from('user_cycles')
      .select('is_premium')
      .eq('user_id', userId)
      .maybeSingle()
    if (error) { console.error('checkIsPremium error:', error.message); return false }
    return data?.is_premium === true
  } catch (err) {
    console.error('checkIsPremium error:', err.message)
    return false
  }
}

export const getPartnerLink = async () => {
  if (!supabase) return null
  const userId = await getAuthUserId()
  if (!userId) return null
  try {
    const { data, error } = await supabase
      .from('partner_links')
      .select('token')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(1)
    if (error) { console.error('getPartnerLink error:', error.message); return null }
    return data?.[0]?.token || null
  } catch (err) {
    console.error('getPartnerLink error:', err.message)
    return null
  }
}

// Cryptographically secure 10-char code from a look-alike-safe alphabet
const PARTNER_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
const PARTNER_CODE_LENGTH = 10;

const generatePartnerCode = () => {
  const bytes = new Uint8Array(PARTNER_CODE_LENGTH);
  window.crypto.getRandomValues(bytes);
  let code = '';
  for (let i = 0; i < PARTNER_CODE_LENGTH; i++) {
    code += PARTNER_ALPHABET[bytes[i] % PARTNER_ALPHABET.length];
  }
  return code;
};

export const createPartnerLink = async () => {
  if (!supabase) return null
  const userId = await getAuthUserId()
  if (!userId) return null

  // Remove any existing link for this user first (one active link at a time)
  try {
    await supabase.from('partner_links').delete().eq('user_id', userId)
  } catch {}

  // Generate with collision check (retry up to 5 times)
  for (let attempt = 0; attempt < 5; attempt++) {
    const token = generatePartnerCode();
    try {
      const { error } = await supabase
        .from('partner_links')
        .insert({ token, user_id: userId })
      if (error) {
        if (error.code === '23505') continue
        console.error('createPartnerLink error:', error.message)
        return null
      }
      return token
    } catch (err) {
      console.error('createPartnerLink error:', err.message)
      return null
    }
  }
  console.error('createPartnerLink: failed after 5 collision retries')
  return null
}

export const revokePartnerLink = async () => {
  if (!supabase) return false
  const userId = await getAuthUserId()
  if (!userId) return false
  try {
    const { error } = await supabase
      .from('partner_links')
      .delete()
      .eq('user_id', userId)
    if (error) {
      console.error('revokePartnerLink error:', error.message)
      return false
    }
    return true
  } catch (err) {
    console.error('revokePartnerLink exception:', err.message)
    return false
  }
}

// ── Events (Supabase-first) ──────────────────────────────────────────────────
// Requires table: CREATE TABLE IF NOT EXISTS user_events (
//   id uuid DEFAULT gen_random_uuid() PRIMARY KEY,
//   user_id uuid REFERENCES auth.users(id) ON DELETE CASCADE,
//   date text NOT NULL,
//   title text NOT NULL,
//   time text,
//   notes text,
//   reminder text,
//   phase text,
//   created_at timestamptz DEFAULT now()
// );
// RLS policies:
//   INSERT: auth.uid() = user_id
//   SELECT: auth.uid() = user_id
//   DELETE: auth.uid() = user_id

export const saveEventToSupabase = async (eventData) => {
  if (!supabase) return null
  const userId = await getAuthUserId()
  if (!userId) return null
  try {
    const row = {
      user_id: userId,
      event_date: eventData.date,
      title: eventData.title,
      event_time: eventData.time || null,
      notes: eventData.notes || null,
      reminder: eventData.reminder || null,
      phase: eventData.phase || null,
    }
    const { data, error } = await supabase
      .from('user_events')
      .insert(row)
      .select('id, event_date, title, event_time, notes, reminder, phase, created_at')
      .single()
    if (error) {
      console.error('saveEventToSupabase error:', error.message)
      return null
    }
    return { ...data, date: data.event_date, time: data.event_time }
  } catch (err) {
    console.error('saveEventToSupabase exception:', err.message)
    return null
  }
}

export const loadEventsFromSupabase = async () => {
  if (!supabase) return null
  const userId = await getAuthUserId()
  if (!userId) return null
  try {
    const { data, error } = await supabase
      .from('user_events')
      .select('id, event_date, title, event_time, notes, reminder, phase, created_at')
      .eq('user_id', userId)
      .order('event_date', { ascending: true })
    if (error) {
      console.error('loadEventsFromSupabase error:', error.message)
      return null
    }
    return (data || []).map(row => ({ ...row, date: row.event_date, time: row.event_time }))
  } catch (err) {
    console.error('loadEventsFromSupabase exception:', err.message)
    return null
  }
}

export const deleteEventFromSupabase = async (eventId) => {
  if (!supabase) return false
  const userId = await getAuthUserId()
  if (!userId) return false
  try {
    const { error } = await supabase
      .from('user_events')
      .delete()
      .eq('id', eventId)
      .eq('user_id', userId)
    if (error) {
      console.error('deleteEventFromSupabase error:', error.message)
      return false
    }
    return true
  } catch (err) {
    console.error('deleteEventFromSupabase exception:', err.message)
    return false
  }
}

export const getEventCountsByDateFromSupabase = async () => {
  if (!supabase) return null
  const userId = await getAuthUserId()
  if (!userId) return null
  try {
    const { data, error } = await supabase
      .from('user_events')
      .select('event_date')
      .eq('user_id', userId)
    if (error) {
      console.error('getEventCountsByDateFromSupabase error:', error.message)
      return null
    }
    const counts = {}
    for (const row of (data || [])) {
      counts[row.event_date] = (counts[row.event_date] || 0) + 1
    }
    return Object.entries(counts).map(([date, count]) => ({ date, count }))
  } catch (err) {
    console.error('getEventCountsByDateFromSupabase exception:', err.message)
    return null
  }
}

// Mark that the user has seen the 4 Seasons intro screen (shown once during onboarding)
// Requires column: ALTER TABLE user_cycles ADD COLUMN IF NOT EXISTS has_seen_seasons_intro boolean DEFAULT false;
export const markSeasonsIntroSeen = async () => {
  if (!supabase) return false
  const userId = await getAuthUserId()
  if (!userId) return false
  try {
    const { error } = await supabase
      .from('user_cycles')
      .update({ has_seen_seasons_intro: true })
      .eq('user_id', userId)
    if (error) {
      console.error('markSeasonsIntroSeen error:', error.message)
      return false
    }
    return true
  } catch (err) {
    console.error('markSeasonsIntroSeen error:', err.message)
    return false
  }
}

// Check if blood drop tooltip has been seen
export const checkBloodDropTooltipSeen = async () => {
  if (!supabase) return true
  const userId = await getAuthUserId()
  if (!userId) return true
  try {
    const { data, error } = await supabase
      .from('user_cycles')
      .select('has_seen_blood_drop_tooltip')
      .eq('user_id', userId)
      .maybeSingle()
    if (error || !data) return true
    return data.has_seen_blood_drop_tooltip === true
  } catch {
    return true
  }
}

// Mark blood drop tooltip as seen
export const markBloodDropTooltipSeen = async () => {
  if (!supabase) return false
  const userId = await getAuthUserId()
  if (!userId) return false
  try {
    await supabase
      .from('user_cycles')
      .update({ has_seen_blood_drop_tooltip: true })
      .eq('user_id', userId)
    return true
  } catch {
    return false
  }
}



// ── Mood Logging ────────────────────────────────────────────────────────────

export const upsertMood = async (date, emoji) => {
  if (!supabase) return false
  const userId = await getAuthUserId()
  if (!userId) return false
  try {
    const { error } = await supabase
      .from('mood_logs')
      .upsert({ user_id: userId, date, emoji }, { onConflict: 'user_id,date' })
    if (error) {
      console.error('Mood upsert error:', error.message)
      return false
    }
    return true
  } catch (err) {
    console.error('Mood upsert error:', err.message)
    return false
  }
}

export const deleteMood = async (date) => {
  if (!supabase) return false
  const userId = await getAuthUserId()
  if (!userId) return false
  try {
    const { error } = await supabase
      .from('mood_logs')
      .delete()
      .eq('user_id', userId)
      .eq('date', date)
    if (error) {
      console.error('Mood delete error:', error.message)
      return false
    }
    return true
  } catch (err) {
    console.error('Mood delete error:', err.message)
    return false
  }
}

export const getMoodsForMonth = async (yearMonth) => {
  if (!supabase) return {}
  const userId = await getAuthUserId()
  if (!userId) return {}
  try {
    const [year, month] = yearMonth.split('-')
    const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate()
    const startDate = `${yearMonth}-01`
    const endDate = `${yearMonth}-${String(lastDay).padStart(2, '0')}`
    const { data, error } = await supabase
      .from('mood_logs')
      .select('date, emoji')
      .eq('user_id', userId)
      .gte('date', startDate)
      .lte('date', endDate)
    if (error) {
      console.error('Mood fetch error:', error.message)
      return {}
    }
    const moodMap = {}
    ;(data || []).forEach(row => { moodMap[row.date] = row.emoji })
    return moodMap
  } catch (err) {
    console.error('Mood fetch error:', err.message)
    return {}
  }
}

// ── Device Tokens for Push Notifications ────────────────────────────────────
// Required Supabase table (run in SQL editor):
//   CREATE TABLE device_tokens (
//     id uuid DEFAULT gen_random_uuid() PRIMARY KEY,
//     user_id uuid REFERENCES auth.users(id) ON DELETE CASCADE,
//     token text NOT NULL,
//     platform text NOT NULL,
//     created_at timestamptz DEFAULT now()
//   );
// RLS policies needed:
//   INSERT: auth.uid() = user_id
//   SELECT: auth.uid() = user_id
//   DELETE: auth.uid() = user_id

export const saveDeviceToken = async (token, platform) => {
  if (!supabase) return false
  const userId = await getAuthUserId()
  if (!userId) {
    console.error('saveDeviceToken error: no authenticated user, cannot save token')
    return false
  }
  // APNs expects lowercase hex; Capacitor's native 'registration' event returns uppercase.
  const normalizedToken = token.toLowerCase()
  try {
    const { error } = await supabase
      .from('device_tokens')
      .upsert(
        { user_id: userId, token: normalizedToken, platform, created_at: new Date().toISOString() },
        { onConflict: 'user_id,token' }
      )

    if (error) {
      console.error('saveDeviceToken error:', error.message)
      return false
    }
    console.log('✅ Device token saved for', platform)
    return true
  } catch (err) {
    console.error('saveDeviceToken error:', err.message)
    return false
  }
}

export const removeDeviceToken = async (token) => {
  if (!supabase) return false
  const userId = await getAuthUserId()
  if (!userId) return false
  try {
    const { error } = await supabase
      .from('device_tokens')
      .delete()
      .eq('user_id', userId)
      .eq('token', token)
    
    if (error) {
      console.error('removeDeviceToken error:', error.message)
      return false
    }
    return true
  } catch (err) {
    console.error('removeDeviceToken error:', err.message)
    return false
  }
}

// ── Notifications Preference ────────────────────────────────────────────────
// Requires column: ALTER TABLE user_cycles ADD COLUMN IF NOT EXISTS notifications_enabled boolean DEFAULT true;

export const getNotificationsEnabled = async () => {
  if (!supabase) return true
  const userId = await getAuthUserId()
  if (!userId) return true
  try {
    const { data, error } = await supabase
      .from('user_cycles')
      .select('notifications_enabled')
      .eq('user_id', userId)
      .maybeSingle()
    if (error || !data) return true
    return data.notifications_enabled !== false
  } catch {
    return true
  }
}

export const setNotificationsEnabled = async (enabled) => {
  if (!supabase) return false
  const userId = await getAuthUserId()
  if (!userId) return false
  try {
    const { error } = await supabase
      .from('user_cycles')
      .update({ notifications_enabled: enabled })
      .eq('user_id', userId)
    if (error) {
      console.error('setNotificationsEnabled error:', error.message)
      return false
    }
    return true
  } catch (err) {
    console.error('setNotificationsEnabled error:', err.message)
    return false
  }
}

// ── Contraceptive Mode Preference ───────────────────────────────────────────
// Requires column: ALTER TABLE user_cycles ADD COLUMN IF NOT EXISTS contraceptive_mode boolean DEFAULT false;

export const getContraceptiveMode = async () => {
  if (!supabase) return false
  const userId = await getAuthUserId()
  if (!userId) return false
  try {
    const { data, error } = await supabase
      .from('user_cycles')
      .select('contraceptive_mode')
      .eq('user_id', userId)
      .maybeSingle()
    if (error || !data) return false
    return data.contraceptive_mode === true
  } catch {
    return false
  }
}

export const setContraceptiveMode = async (enabled) => {
  if (!supabase) return false
  const userId = await getAuthUserId()
  if (!userId) return false
  try {
    const { error } = await supabase
      .from('user_cycles')
      .update({ contraceptive_mode: enabled })
      .eq('user_id', userId)
    if (error) {
      console.error('setContraceptiveMode error:', error.message)
      return false
    }
    return true
  } catch (err) {
    console.error('setContraceptiveMode error:', err.message)
    return false
  }
}

// ── Timezone Sync ────────────────────────────────────────────────────────────
// Requires column: ALTER TABLE user_cycles ADD COLUMN IF NOT EXISTS timezone text;
// Keeps user_cycles.timezone in sync with the device's IANA timezone so reminders
// (Phase 4) can be scheduled at the correct local time. Runs on every app startup,
// on both web and native — Intl timezone detection works identically in both.

export const syncUserTimezone = async () => {
  if (!supabase) return
  const userId = await getAuthUserId()
  if (!userId) return

  let detected = ''
  try {
    detected = Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  } catch (err) {
    console.warn('[syncUserTimezone] Intl detection failed:', err.message)
  }
  if (!detected) {
    console.warn('[syncUserTimezone] no timezone detected — leaving stored value untouched')
    return
  }
  console.log('[syncUserTimezone] detected timezone:', detected)

  try {
    const { data, error } = await supabase
      .from('user_cycles')
      .select('timezone')
      .eq('user_id', userId)
      .maybeSingle()
    if (error || !data) {
      console.log('[syncUserTimezone] no user_cycles row yet — skipping sync')
      return
    }
    if (data.timezone === detected) {
      console.log('[syncUserTimezone] stored timezone already up to date — no write needed')
      return
    }
    const { error: updateError } = await supabase
      .from('user_cycles')
      .update({ timezone: detected })
      .eq('user_id', userId)
    if (updateError) {
      console.error('[syncUserTimezone] update error:', updateError.message)
      return
    }
    console.log('[syncUserTimezone] updated stored timezone from', data.timezone, 'to', detected)
  } catch (err) {
    console.error('[syncUserTimezone] exception:', err.message)
  }
}

// ── Onboarding Completed Flag ───────────────────────────────────────────────
// Requires column: ALTER TABLE user_cycles ADD COLUMN IF NOT EXISTS onboarding_completed boolean DEFAULT false;

export const checkOnboardingCompleted = async () => {
  if (!supabase) return false
  const userId = await getAuthUserId()
  if (!userId) return false
  try {
    const { data, error } = await supabase
      .from('user_cycles')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle()
    if (error || !data) return false
    // Explicit flag (once column is added)
    if (data.onboarding_completed === true) return true
    // Backward compat: existing users with cycle data are treated as completed
    if (data.last_period_start_date) return true
    return false
  } catch {
    return false
  }
}

export const markOnboardingCompleted = async () => {
  if (!supabase) return false
  const userId = await getAuthUserId()
  if (!userId) return false
  try {
    const { error } = await supabase
      .from('user_cycles')
      .update({ onboarding_completed: true })
      .eq('user_id', userId)
    if (error) {
      console.error('markOnboardingCompleted error:', error.message)
      return false
    }
    return true
  } catch (err) {
    console.error('markOnboardingCompleted error:', err.message)
    return false
  }
}
